import { Menu } from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import { CSSProperties, useContext, useMemo, useRef, useState } from "react";
import { useReduxDispatch, useReduxSelector } from "../../../../redux/hooks";
import { updateIframeSrc } from "../../../../slices/RevelationSlice";
import { setContextMenuOpen } from "../../NavSlice";
import useEventListener from "@use-it/event-listener";
import { IProfileTransform } from "../../../../types/INavTransform";
import { IUserProfile, NavItems } from "../../../../types/User/IUserProfile";
import { AddTextAndItemsAttrToHamburgerItems } from "../../../../utils/utils";
import { TicketLocationState } from "../../../Ticket/TicketRoute";
import { useLocation, useNavigate } from "react-router-dom";
import { nanoid } from "nanoid";
import ProductTour, { ProductTourProps } from "./ProductTour";
import TrialTour, { TrialTourProps } from "./TrialTour";
import { RevUserContext } from "../../../../context/UserContext";
import {
  useGetTenantQuery,
  useReleaselicenseMutation,
} from "../../../../redux/api/BaseApi";
import { oAuthSettings } from "../../../Auth/AuthSettings";
import { createUserManager } from "redux-oidc";
import { SplitButton } from "@progress/kendo-react-buttons";
import { ProfileNavProps } from "../ProfileNav";
import { UserApi } from "../../../../redux/api/UserApi";

const PROFILE = "Profile";
const LOG_OUT = "Log Out";
const HELP = "Help";

const ProfileDropdown = (props: ProfileNavProps) => {
  // console.log(props);
  const data = useContext<IUserProfile>(RevUserContext);
  const user = useReduxSelector((state) => state.auth.user);
  const { data: tenant } = useGetTenantQuery();
  const [dismissFeature] = UserApi.useDismissIntroMutation();
  const {
    Profile_Menu_Items,
    Id,
    Features,
    Profile_Image_Url,
    Help_Url,
    Whats_New_Tour_Url,
    Show_Tour,
    Show_Trial_Tour
  } = data;
  const { Profile_Images } = Features;
  let location = useLocation();
  let navigate = useNavigate();
  const anchor = useRef<HTMLButtonElement | null>(null);
  const contextMenuOpen = useReduxSelector(
    (state) => state.nav.contextMenuOpen
  );
  const [productTourPopupVisible, setProductTourPopupVisible] = useState<boolean>(false);
  const [trialTourPopupVisible, setTrialTourPopupVisible] = useState<boolean>(Show_Trial_Tour);
  const [whatsNewTourPopupVisible, setWhatsNewTourPopupVisible] = useState<boolean>(Show_Tour);
  const dispatch = useReduxDispatch();

  const productTourProps: ProductTourProps = {
    width: 989,
    url: Profile_Menu_Items.find((item) => item.Name === "ProductTour")?.Url,
    onClose: () => setProductTourPopupVisible(false),
  };
  const trialTourProps: TrialTourProps = {
    url: Profile_Menu_Items.find((item) => item.Name === "TrialTour")?.Url,
    onClose: () => {
      setTrialTourPopupVisible(false)
      //Update user settings via API
      dismissFeature(99).unwrap();
    },
  };

  const whatsNewTourProps: ProductTourProps = {
    width: 992,
    height: 576,
    url: Whats_New_Tour_Url,
    onClose: () => {
      setWhatsNewTourPopupVisible(false);

      //Update user settings via API
      dismissFeature(101).unwrap();
    }
  };

  // Additional Items
  const buildItemList = (items: NavItems[]) => {
    const additionalItems = [
      // {
      //   text: "Admin",
      //   Url: "/adminUX/DynamicUI/Default.aspx?PageName=HelpdeskSetup",
      //   access: Admin_Area,
      // },
      // { text: "Numbers", Url: "/Numbers/Default.aspx", access: Numbers },
      { text: HELP, Url: Help_Url, access: true },
      //{ text: LOG_OUT, Url: LOG_OUT, access: true },
    ];
    const regularItems = AddTextAndItemsAttrToHamburgerItems(items);
    additionalItems.forEach((item) => {
      // @ts-ignore
      if (item.access) regularItems.push(item);
    });
    return regularItems;
  };
  const addTextAndItemsToProps = useMemo(
    () => buildItemList(Profile_Menu_Items),
    [Profile_Menu_Items]
  );

  // logout button

  const [releaseLicense] = useReleaselicenseMutation();
  const logMeOut = async () => {
    console.log("sign out");
    await releaseLicense(null);
    console.log("user.id_token=", user.id_token);
    let settings = oAuthSettings;
    if (tenant?.Authority) settings.authority = tenant?.Authority;
    //First release the current license, THEN release the token and redirect to the logout page.
    const userManager = createUserManager(settings);
    await userManager.signoutRedirect({ id_token_hint: user.id_token });
  };

  function handleItemRender(menuItem: IProfileTransform) {
    const { item, itemId } = menuItem;
    const buttonStyle: CSSProperties = {
      color: "black",
      fontSize: "small",
    };
    return (
      <button key={itemId} style={buttonStyle}>
        {item.text}
      </button>
    );
  }

  function handleProfileClick() {
    navigate("/");

    dispatch(updateIframeSrc(`/contacts/contactinfo.aspx?contact_id=${Id}`));
  }
  useEventListener("click", () => {
    if (document.activeElement !== anchor.current && contextMenuOpen !== "")
      dispatch(setContextMenuOpen(""));
  });

  function handleOnClick(url: string) {
    console.log("click", url);
    if (url) {
      if (url === LOG_OUT) {
        logMeOut();
      } else if (url === Help_Url) {
        window.open(Help_Url, "_blank", "noopener,noreferrer");
      } else if (url.indexOf("./") === 0) {
        //internal URL, not IFrame
        const ticketState = {
          background: location,
        } as unknown as TicketLocationState;
        navigate(url, { state: ticketState });
      } else if (url.toLowerCase() === productTourProps?.url?.toLowerCase())
        setProductTourPopupVisible(true);
      else if (url.toLowerCase().includes("free-trial"))
        setTrialTourPopupVisible(true);
      else if (url.indexOf("http") === 0)
        window.location = url as unknown as Location;
      else {
        navigate("/");
        //console.log("dispatch", `${trimChar(siteBaseUrl, "/")}${url}`);
        dispatch(
          updateIframeSrc(
            `${url}${url.indexOf("?") > -1 ? "&" : "?"}d=${nanoid()}`
          )
        );
      }
    }
  }

  return (
    <>
      {Profile_Images ? (
        <>
          <SplitButton title="View profile" className={(props.navStyle === 0 || props.navStyle === 1) ? "profile-image-button" : "profile-image-button-small"} imageUrl={
            !Profile_Image_Url ? './images/placeholder.png' :
              Profile_Image_Url?.indexOf("/images") === 0
                ? process.env.PUBLIC_URL + Profile_Image_Url
                : Profile_Image_Url
          } items={addTextAndItemsToProps} fillMode={"flat"} onButtonClick={(e) => {
            handleProfileClick()
            //unfocus
            e.target.setState(s => {
              return { ...s, focused: false }
            });
          }}
            onItemClick={(e) => {
              console.log('handleOnClick', e.target.state);
              //unfocus
              e.target.setState(s => {
                return { ...s, focused: false }
              });
              //const navItem: NavItems = e.item as NavItems;
              handleOnClick(e.item?.Url);
            }} />
        </>
      ) : (
        <button
          ref={anchor}
          title="Edit Profile"
          onClick={handleProfileClick}
          onContextMenu={(e) => {
            e.preventDefault();
            dispatch(setContextMenuOpen(PROFILE));
          }}
          className={!Profile_Images ? "secondary-nav" : ""}
        >
          <div className="relative">
            <img
              className="indicator"
              src="./images/context-menu-indicator.png"
              alt="context menu indicator"
            ></img>
            <span className="k-font-icon k-i-user"></span>
          </div>
        </button>

      )}
      <Popup
        anchor={anchor.current}
        show={contextMenuOpen === PROFILE}
        anchorAlign={{ horizontal: "right", vertical: "bottom" }}
        popupAlign={{ horizontal: "right", vertical: "top" }}
      >
        <Menu
          className="profile-dropdown-menu"
          vertical
          style={{
            backgroundColor: "#e5e5e4",
            border: "solid 1px #73a942",
          }}
          itemRender={(item: IProfileTransform) => handleItemRender(item)}
          items={addTextAndItemsToProps}
          onSelect={(e) => {
            const navItem: NavItems = e.item as NavItems;
            handleOnClick(navItem.Url);
          }}
        // For Debugging
        // hoverCloseDelay={1000 * 60 * 60}
        />
      </Popup>
      {productTourPopupVisible ? (
        <ProductTour props={productTourProps} />
      ) : null}
      {trialTourPopupVisible ? <TrialTour props={trialTourProps} /> : null}
      {whatsNewTourPopupVisible && <ProductTour props={whatsNewTourProps} />}
      {/* Left here for Testing Purposes */}
      {/* <ProductTour props={productTourProps} /> */}
      {/* <TrialTour props={trialTourProps} /> */}

    </>
  );
};
export default ProfileDropdown;
